// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
// import React, { Fragment, useState } from "react"; // Store Custom Code
// import { connect } from "react-redux"; // Store Custom Code
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { orderSummaryProps } from "@store/Themes/common/props/order";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import removeCartLineSelectedWhsePickType from "@store/Framework/Store/Store/Pages/Cart/Handlers/RemoveCartLineSelectedWhsePickType";
import setIsCSOrder from "@store/Framework/Store/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsCSOrder";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import get from "lodash/get";
import isNil from "lodash/isNil";
import some from "lodash/some";
import React, { Fragment, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
/* Store Custom Code - End */

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    // Starter Custom Code
    overridedStyles?: CartTotalDisplayStyles;
    showCSToggle?: boolean; // Store Custom Code
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    isCSOrder: state.store.pages.checkoutReviewAndSubmit.isCSOrder, // Store Custom Code
});

const mapDispatchToProps = { setIsCSOrder, removeCartLineSelectedWhsePickType }; // Store Custom Code

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>; // Store Custom Code

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
    title?: TypographyProps;
    titleWrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    realTimeText?: TypographyProps;
    // Store Custom Code
    counterSaleWrapper?: InjectableCss;
    counterSaleCheckbox?: CheckboxPresentationProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    subtotalLabel: orderSummaryProps.itemLabel,
    subtotalValue: orderSummaryProps.itemValue,
    promotionLabel: orderSummaryProps.itemLabel,
    promotionValue: orderSummaryProps.itemValue,
    discountsLabel: orderSummaryProps.itemLabel,
    discountsValue: orderSummaryProps.itemValue,
    shippingAndHandlingLabel: orderSummaryProps.itemLabel,
    shippingAndHandlingValue: orderSummaryProps.itemValue,
    taxLabel: orderSummaryProps.itemLabel,
    taxValue: orderSummaryProps.itemValue,
    totalLabel: orderSummaryProps.totalLabel,
    totalValue: orderSummaryProps.totalValue,
    wrapper: orderSummaryProps.wrapper,
    titleWrapper: orderSummaryProps.titleWrapper,
    title: orderSummaryProps.title,
    itemWrapper: orderSummaryProps.itemWrapper,
    realTimeText: orderSummaryProps.realTimeText,
    // Store Custom Code
    counterSaleWrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            padding: ${pxToRem(16)};
        `,
    },
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    // Starter Custom Code
    overridedStyles,
    // Store Custom Code
    isCSOrder,
    setIsCSOrder,
    showCSToggle = true,
    removeCartLineSelectedWhsePickType,
}: Props) => {
    const styles = useExpandProps(cartTotalDisplayStyles, extendedStyles, overridedStyles); // Starter Custom Code

    /* Store Custom Code - Start */
    useEffect(() => {
        if (
            !cart?.properties.canCounterSales ||
            (cart?.properties.canCounterSales && cart?.properties.canCounterSales.toLowerCase() === "false")
        ) {
            setIsCSOrder({ isCSOrder: false });
            clearWhsePickType();
        }
    }, [cart?.properties]);

    const clearWhsePickType = () => {
        if (!cart) {
            return;
        }
        const hasCSOrderLines = some(cart?.cartLines, line => {
            const whsePickType = get(line, "properties.whsePickType", null);
            return !isNil(whsePickType) && whsePickType !== "";
        });
        if (hasCSOrderLines) {
            removeCartLineSelectedWhsePickType({
                onSuccess: (successProps: any) => {},
            });
        }
    };

    const IsCSOrderChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        const isCSOrderToSet = !isCSOrder;
        setIsCSOrder({ isCSOrder: isCSOrderToSet });
        if (!isCSOrderToSet) {
            clearWhsePickType();
        }
    };
    /* Store Custom Code - End */

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    if (!cart /* Starter Custom Code */) {
        return (
            <StyledWrapper {...styles.wrapper}>
                {/* Starter Custom Code - Start */}
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>{translate("Order Summary")}</Typography>
                </StyledWrapper>
                {/* Starter Custom Code - End */}
                {showTaxAndShipping && (
                    <StyledWrapper {...styles.itemWrapper}>
                        <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        {/* Starter Custom Code - Start */}
                        <Typography {...styles.totalValue}>
                            <Typography {...styles.realTimeText} />
                        </Typography>
                        {/* Starter Custom Code - End */}
                    </StyledWrapper>
                )}
                {!showTaxAndShipping && (
                    <StyledWrapper {...styles.itemWrapper}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                        </Typography>
                        {/* Starter Custom Code - Start */}
                        <Typography {...styles.totalValue}>
                            <Typography {...styles.realTimeText} />
                        </Typography>
                        {/* Starter Custom Code - End */}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    if (isCartEmpty) {
        return (
            <StyledWrapper {...styles.wrapper}>
                {/* Starter Custom Code - Start */}
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>{translate("Order Summary")}</Typography>
                </StyledWrapper>
                {/* Starter Custom Code - End */}
                {showTaxAndShipping && (
                    <StyledWrapper {...styles.itemWrapper} /* Starter Custom Code */>
                        <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        {/* Starter Removed Code - StyledWrapper */}
                        <Typography {...styles.totalValue}>{`${cart.currencySymbol} -`}</Typography>
                    </StyledWrapper>
                )}
                {!showTaxAndShipping && (
                    <StyledWrapper {...styles.itemWrapper} /* Starter Custom Code */>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                        </Typography>
                        {/* Starter Removed Code - StyledWrapper */}
                        <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    const filteredOrderPromotions = orderPromotions?.filter(o => o.amount > 0);
    const filteredShippingPromotions = shippingPromotions?.filter(o => o.amount > 0);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`cartTotal_${cart.id}`}>
            {/* Starter Custom Code - Start */}
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>{translate("Order Summary")}</Typography>
            </StyledWrapper>
            {/* Starter Custom Code - End */}
            <StyledWrapper {...styles.itemWrapper} /* Starter Custom Code */>
                <Typography {...styles.subtotalLabel}>
                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                </Typography>
                {/* Starter Removed Code - GridItem */}
                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                    {/* Store Custom Code */ cart.isAuthenticated ? cart.orderSubTotalDisplay : "-"}
                </Typography>
            </StyledWrapper>
            {filteredOrderPromotions &&
                filteredOrderPromotions.map(promotion => (
                    <StyledWrapper key={promotion.id} {...styles.itemWrapper} /* Starter Custom Code */>
                        <Typography {...styles.promotionLabel} data-test-selector="cartTotal_orderPromotionName">
                            {`${translate("{0}:", translate("Promotion"))} ${promotion.name}` /* Starter Custom Code */}
                        </Typography>
                        {/* Starter Removed Code - GridItem */}
                        <Typography {...styles.promotionValue} data-test-selector="cartTotal_orderPromotionValue">
                            {`-${promotion.amountDisplay}`}
                        </Typography>
                    </StyledWrapper>
                ))}
            {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && (
                <StyledWrapper {...styles.itemWrapper} /* Starter Custom Code */>
                    <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}</Typography>
                    {/* Starter Removed Code - GridItem */}
                    <Typography
                        {...styles.shippingAndHandlingValue}
                        data-test-selector="cartTotal_shippingAndHandlingAmount"
                    >
                        {cart.shippingAndHandlingDisplay}
                    </Typography>
                </StyledWrapper>
            )}
            {filteredShippingPromotions &&
                filteredShippingPromotions.map(promotion => (
                    <StyledWrapper key={promotion.id} {...styles.itemWrapper} /* Starter Custom Code */>
                        <Typography {...styles.promotionLabel}>
                            {`${translate("{0}:", translate("Promotion"))} ${promotion.name}` /* Starter Custom Code */}
                        </Typography>
                        {/* Starter Removed Code - GridItem */}
                        <Typography {...styles.promotionValue} data-test-selector="cartTotal_shippingPromotionValue">
                            {`-${promotion.amountDisplay}`}
                        </Typography>
                    </StyledWrapper>
                ))}
            {showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                <StyledWrapper {...styles.itemWrapper} /* Starter Custom Code */>
                    <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                    {/* Starter Removed Code - GridItem */}
                    <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                        {cart.totalTaxDisplay}
                    </Typography>
                </StyledWrapper>
            )}
            {showTaxAndShipping &&
                [...cart.customerOrderTaxes!]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <StyledWrapper key={key} {...styles.itemWrapper} /* Starter Custom Code */>
                                {/* Starter Removed Code - Fragment */}
                                <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                {/* Starter Removed Code - GridItem */}
                                <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                            </StyledWrapper>
                        );
                    })}
            {showTaxAndShipping && (
                <StyledWrapper {...styles.itemWrapper} /* Starter Custom Code */>
                    <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                    {/* Starter Removed Code - GridItem */}
                    <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                        {cart.orderGrandTotalDisplay}
                    </Typography>
                </StyledWrapper>
            )}
            {discountTotal !== undefined && discountTotal > 0 && (
                <StyledWrapper {...styles.itemWrapper} /* Starter Custom Code */>
                    <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    {/* Starter Removed Code - GridItem */}
                    <Typography {...styles.discountsValue} data-test-selector="cartTotal_totalDiscounts">
                        {`${translate("You saved")} `}{" "}
                        <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                    </Typography>
                </StyledWrapper>
            )}
            {/* Store Custom Code - Start */}
            {showCSToggle &&
                cart.properties.canCounterSales &&
                cart.properties.canCounterSales.toLowerCase() === "true" && (
                    <StyledWrapper {...styles.counterSaleWrapper}>
                        <Checkbox checked={isCSOrder} onChange={IsCSOrderChangeHandler}>
                            {translate("Is Counter Sale")}
                        </Checkbox>
                    </StyledWrapper>
                )}
            {/* Store Custom Code - End */}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CartTotalDisplay); // Store Custom Code
